import type { ClipboardEvent, KeyboardEvent } from 'react'
import { useState, useRef } from 'react'
import { OTP_NON_DIGITS_REGEX, OTP_LENGTH } from './constants'
import { KEYBOARD_KEY } from '@/shared/constants/keyboard'

export function useOtpInput({
  onChange,
}: {
  onChange: (code: string) => void
  loading?: boolean
}) {
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(''))
  const refs = useRef<HTMLInputElement[]>([])

  function handleChange(index: number, value: string) {
    const cleaned = value.replace(OTP_NON_DIGITS_REGEX, '')
    if (!cleaned) {
      const newDigits = [...digits]
      newDigits[index] = ''
      setDigits(newDigits)
      onChange(newDigits.join(''))
      return
    }

    if (cleaned.length > 1) {
      const pasted = cleaned.slice(0, OTP_LENGTH)
      const newDigits: string[] = [...digits]
      pasted.split('').forEach((char, i) => {
        newDigits[i] = char
      })
      setDigits(newDigits)
      refs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus()
      onChange(newDigits.join(''))
      return
    }

    const newDigits: string[] = [...digits]
    newDigits[index] = cleaned
    setDigits(newDigits)
    if (index < OTP_LENGTH - 1) {
      refs.current[index + 1]?.focus()
    }
    onChange(newDigits.join(''))
  }

  function handleKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === KEYBOARD_KEY.BACKSPACE) {
      if (digits[index]) {
        const newDigits = [...digits]
        newDigits[index] = ''
        setDigits(newDigits)
        onChange(newDigits.join(''))
      } else if (index > 0) {
        refs.current[index - 1]?.focus()
      }
    }
  }

  function applyPasted(raw: string) {
    const pasted = raw.replace(OTP_NON_DIGITS_REGEX, '').slice(0, OTP_LENGTH)
    if (!pasted) return
    const newDigits: string[] = [...digits]
    pasted.split('').forEach((char, i) => {
      newDigits[i] = char
    })
    setDigits(newDigits)
    refs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus()
    onChange(newDigits.join(''))
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    const raw = e.clipboardData?.getData('text') ?? ''
    if (raw) {
      e.preventDefault()
      applyPasted(raw)
      return
    }
  }

  return { digits, refs, handleChange, handleKeyDown, handlePaste }
}
